import {ellipsis} from '../util/ellipsis';
import fadeIn from '../util/fade-in';
import fadeOut from '../util/fade-out';
import hideElement from '../util/hide-element';
import showElement from '../util/show-element';
import scrollTo from '../util/scroll-to';
import {register} from '../vendor/theme-scripts/theme-sections';
import Media from '../media/media';
import pickupAvailability from '../features/pickup-availability';
import {productFormSection} from '../features/product-form';
import {swatchSection} from '../features/swatch';
import {tooltipSection} from '../features/tooltip';
import {popoutSection} from '../features/popout';
import {UpsellProduct} from '../features/upsell-product';
import {productStickySection} from '../features/product-sticky';
import accordions from '../features/accordion';
import QuantityCounter from '../globals/quantity-handle';
import copyClipboard from '../features/copy-clipboard';
import tabs from '../features/tabs';
import scrollToElement from '../features/scroll-to-element';
import * as a11y from '../vendor/theme-scripts/theme-a11y';

window.theme.variables = {
  bpSmall: false,
};

const selectors = {
  addToCart: '[data-add-to-cart]',
  priceWrapper: '[data-price-wrapper]',
  productImage: '[data-product-image]',
  productJson: '[data-product-json]',
  form: '[data-product-form]',
  thumbs: '[data-product-thumbs]',
  dataSectionId: 'data-section-id',
  dataTallLayout: 'data-tall-layout',
  dataCartBar: 'data-cart-bar',
  dataProductShare: '[data-product-share]',
  dataProductShareValue: 'data-product-share',
  dataProductShareTitleValue: 'data-product-share-title',
  cartBar: '#cart-bar',
  productSubmitAdd: '.product__submit__add',
  cartBarAdd: 'data-add-to-cart-bar',
  cartBarScroll: 'data-cart-bar-scroll',
  templateProduct: '#template-product',
  siteFooterWrapper: '.site-footer-wrapper',
  toggleTruncateHolder: '[data-truncated-holder]',
  toggleTruncateButton: '[data-truncated-button]',
  toggleTruncateContent: '[data-truncated-content]',
  toggleTruncateContentAttr: 'data-truncated-content',
  upsellButton: '[data-upsell-btn]',
  upsellButtonText: '[data-upsell-btn-text]',
  dataModalButton: 'data-product-popup',
  modalScrollContainer: '[data-tabs-holder]',
  formWrapper: '[data-form-wrapper]',
};

const classes = {
  expanded: 'is-expanded',
  visible: 'is-visible',
  loading: 'is-loading',
  siteFooterPush: 'site-footer--push',
  hasPopup: 'has-popup',
};

const sections = {};

/**
 * Product section constructor.
 * @param {string} container - selector for the section container DOM element
 */
class Product {
  constructor(section) {
    this.section = section;
    this.container = section.container;
    this.id = this.container.getAttribute(selectors.dataSectionId);
    this.tallLayout = this.container.getAttribute(selectors.dataTallLayout) === 'true';
    this.thumbs = this.container.querySelector(selectors.thumbs);
    this.shareButton = this.container.querySelector(selectors.dataProductShare);
    this.upsellButton = this.container.querySelector(selectors.upsellButton);
    this.truncateElementHolder = this.container.querySelector(selectors.toggleTruncateHolder);
    this.truncateElement = this.container.querySelector(selectors.toggleTruncateContent);
    this.modalButton = this.container.querySelectorAll(`[${selectors.dataModalButton}]`);
    this.formWrapper = this.container.querySelector(selectors.formWrapper);
    this.cartBarExist = this.container.getAttribute(selectors.dataCartBar) === 'true';
    this.scrollEvent = (e) => this.scrollEvents(e);
    this.resizeEvent = (e) => this.resizeEvents(e);
    this.unlockTimer = 0;
    this.accessibility = a11y;

    // Record recently viewed products when the product page is loading
    Shopify.Products.recordRecentlyViewed();

    this.shareToggle();

    if (this.truncateElementHolder && this.truncateElement) {
      setTimeout(() => this.truncateText(), 50);
    }

    if (this.upsellButton) {
      this.calcUpsellButtonDemensions();
    }

    if (this.upsellButton || (this.truncateElementHolder && this.truncateElement)) {
      document.addEventListener('theme:resize', this.resizeEvent);
    }

    // Stop parsing if we don't have the product json script tag when loading
    // section in the Theme Editor
    const productJson = this.container.querySelector(selectors.productJson);
    if ((productJson && !productJson.innerHTML) || !productJson) {
      const counter = new QuantityCounter(this.container);
      counter.init();
      return;
    }

    this.form = this.container.querySelector(selectors.form);

    this.init();

    if (this.cartBarExist) {
      this.initCartBar();
    }

    if (this.cartBarExist) {
      document.addEventListener('theme:scroll', this.scrollEvent);
    }

    if (this.modalButton.length > 0) {
      this.productPopup();
    }
  }

  init() {
    theme.mediaInstances[this.id] = new Media(this.section);
    theme.mediaInstances[this.id].init();
  }

  scrollEvents(e) {
    if (this.cartBarExist) {
      this.cartBarScroll();
    }
  }

  resizeEvents(e) {
    if (this.truncateElementHolder && this.truncateElement) {
      this.truncateText();
    }

    if (this.upsellButton) {
      this.calcUpsellButtonDemensions();
    }
  }

  productPopup() {
    this.modalButton.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const modal = document.querySelector(`#${button.getAttribute(selectors.dataModalButton)}`);
        const modalScrollContainer = modal.querySelector(selectors.modalScrollContainer);

        if (window.getComputedStyle(modal).display !== 'none') {
          fadeOut(modal);
          this.formWrapper.classList.remove(classes.hasPopup);
          this.accessibility.removeTrapFocus();

          if (this.unlockTimer) {
            clearTimeout(this.unlockTimer);
          }
          // delay scroll unlock to prevent content shifting
          this.unlockTimer = setTimeout(() => {
            document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
          }, 300);
        }

        if (window.getComputedStyle(modal).display === 'none') {
          fadeIn(modal);
          this.formWrapper.classList.add(classes.hasPopup);
          document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: modalScrollContainer}));
          this.accessibility.trapFocus(modal);
        }
      });
    });
  }

  calcUpsellButtonDemensions() {
    const upsellButtonText = this.upsellButton.querySelector(selectors.upsellButtonText);
    if (upsellButtonText) {
      this.upsellButton.style.setProperty('--btn-text-width', `${upsellButtonText.clientWidth}px`);
    }
  }

  truncateText() {
    if (this.truncateElementHolder.classList.contains(classes.visible)) return;
    const styles = this.truncateElement.querySelectorAll('style');
    if (styles.length) {
      styles.forEach((style) => {
        this.truncateElementHolder.prepend(style);
      });
    }

    const truncateElementCloned = this.truncateElement.cloneNode(true);
    const truncateElementClass = this.truncateElement.getAttribute(selectors.toggleTruncateContentAttr);
    const truncateNextElement = this.truncateElement.nextElementSibling;
    if (truncateNextElement) {
      truncateNextElement.remove();
    }

    this.truncateElement.parentElement.append(truncateElementCloned);

    const truncateAppendedElement = this.truncateElement.nextElementSibling;
    truncateAppendedElement.classList.add(truncateElementClass);
    truncateAppendedElement.removeAttribute(selectors.toggleTruncateContentAttr);

    showElement(truncateAppendedElement);

    ellipsis(truncateAppendedElement, 5, {
      replaceStr: '',
      delimiter: ' ',
    });

    hideElement(truncateAppendedElement);

    if (this.truncateElement.innerHTML !== truncateAppendedElement.innerHTML) {
      this.truncateElementHolder.classList.add(classes.expanded);
    } else {
      truncateAppendedElement.remove();
      this.truncateElementHolder.classList.remove(classes.expanded);
    }

    this.toggleTruncatedContent(this.truncateElementHolder);
  }

  toggleTruncatedContent(holder) {
    const toggleButton = holder.querySelector(selectors.toggleTruncateButton);
    if (toggleButton) {
      toggleButton.addEventListener('click', (e) => {
        e.preventDefault();
        holder.classList.remove(classes.expanded);
        holder.classList.add(classes.visible);
      });
    }
  }

  shareToggle() {
    if (this.shareButton) {
      this.shareButton.addEventListener('click', function () {
        const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

        if (navigator.share && windowWidth <= 1024) {
          const shareTitle = this.hasAttribute(selectors.dataProductShareTitleValue) ? this.getAttribute(selectors.dataProductShareTitleValue) : window.location.hostname;
          const shareUrl = this.hasAttribute(selectors.dataProductShareValue) ? this.getAttribute(selectors.dataProductShareValue) : window.location.href;

          navigator
            .share({
              title: shareTitle,
              url: shareUrl,
            })
            .then(() => {
              console.log('Thanks for sharing!');
            })
            .catch(console.error);
        } else {
          this.parentElement.classList.toggle(classes.expanded);
        }
      });
    }
  }

  initCartBar() {
    const cartBar = document.querySelector(selectors.cartBar);
    const cartBarBtn = cartBar.querySelector(selectors.productSubmitAdd);

    // Submit product form on cart bar button click
    if (cartBarBtn) {
      cartBarBtn.addEventListener('click', (e) => {
        e.preventDefault();

        if (e.target.hasAttribute(selectors.cartBarAdd)) {
          if (theme.settings.cartDrawerEnabled) {
            e.target.classList.add(classes.loading);
            e.target.setAttribute('disabled', 'disabled');
          }

          this.form.querySelector(selectors.addToCart).dispatchEvent(
            new Event('click', {
              bubbles: true,
            })
          );
        } else if (e.target.hasAttribute(selectors.cartBarScroll)) {
          this.scrollToTop();
        }
      });

      if (cartBarBtn.hasAttribute(selectors.cartBarAdd)) {
        document.addEventListener('theme:product:button-error', () => this.scrollToTop());
      }

      if (theme.settings.cartDrawerEnabled) {
        document.addEventListener('theme:product:button-update', () => {
          if (cartBarBtn && cartBarBtn.classList.contains(classes.loading)) {
            cartBarBtn.classList.remove(classes.loading);
            cartBarBtn.removeAttribute('disabled');
          }
        });
      }
    }

    this.cartBar = cartBar;
  }

  scrollToTop() {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const isDesktop = windowWidth >= window.theme.sizes.small;
    const scrollTarget = isDesktop ? this.container : this.form;

    scrollTo(scrollTarget.getBoundingClientRect().top);
  }

  cartBarScroll() {
    const scrolled = window.pageYOffset;
    const element = theme.variables.productPageSticky && this.formWrapper ? this.formWrapper : this.form;

    if (element && this.cartBar) {
      const siteFooter = document.querySelector(selectors.siteFooterWrapper);
      const formOffset = element.offsetTop;
      const formHeight = element.offsetHeight;
      const checkPosition = scrolled > formOffset + formHeight;

      this.cartBar.classList.toggle(classes.visible, checkPosition);
      if (siteFooter) {
        siteFooter.classList.toggle(classes.siteFooterPush, checkPosition);
        siteFooter.style.marginBottom = siteFooter.classList.contains(classes.siteFooterPush) ? `${this.cartBar.offsetHeight}px` : '0';
      }
    }
  }

  onUnload() {
    if (this.upsellButton || (this.truncateElementHolder && this.truncateElement)) {
      document.removeEventListener('theme:resize', this.resizeEvent);
    }

    if (this.cartBarExist) {
      document.removeEventListener('theme:scroll', this.scrollEvent);
    }
  }
}

const productSection = {
  onLoad() {
    sections[this.id] = new Product(this);
  },
  onUnload(e) {
    sections[this.id].onUnload(e);
  },
};

register('product', [productSection, pickupAvailability, productFormSection, swatchSection, tooltipSection, popoutSection, tabs, accordions, copyClipboard, scrollToElement, productStickySection]);

if (!customElements.get('upsell-product')) {
  customElements.define('upsell-product', UpsellProduct);
}
